/**
 * The left side: the host's photo, one search, the live call while one is on, What's new while something
 * is new, the host, Files, and the person's rooms with their Everyone, Announcements and circles.
 */
import { type ReactNode, useEffect, useState } from "react";
import { BrandGlyph } from "../../components/BrandMark";
import { Face } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { usePage } from "./context";
import { Item, LiveCard } from "./Parts";
import { hasNews, type Place } from "./talk";

export function MadeWith(): ReactNode {
  return (
    <p className="jp-made">
      <BrandGlyph size={16} />
      Made with Flashroom
    </p>
  );
}

/** The host's photo, with a green dot while they are here. */
export function HostAvatar({ small }: { small?: boolean }): ReactNode {
  const { host, online } = usePage();
  return (
    <span className={small ? "jp-av gq-av s" : "jp-av"}>
      {host.avatar ? <img src={host.avatar} alt="" /> : <Face name={host.name} size={small ? 34 : 64} />}
      {online ? <i title="Online" /> : null}
    </span>
  );
}

/** The search box: typing opens Search, and the cross goes back to where the person was. */
function SearchBox(): ReactNode {
  const { place, goPlace, guest } = usePage();
  const q = place.kind === "search" ? place.q : "";
  const [text, setText] = useState(q);
  useEffect(() => setText(q), [q]);
  const search = (next: string) => goPlace({ kind: "search", q: next }, true);
  return (
    <label className={q ? "jp-q on" : "jp-q"}>
      <Icon name="search" />
      <input
        value={text}
        placeholder="Search messages and files"
        aria-label="Search messages and files"
        onFocus={() => place.kind !== "search" && search(text)}
        onChange={(e) => {
          setText(e.target.value);
          search(e.target.value);
        }}
      />
      {text ? (
        <button type="button" aria-label="Clear the search" onClick={() => goPlace(hasNews(guest) ? { kind: "new" } : { kind: "direct" })}>
          <Icon name="x" />
        </button>
      ) : null}
    </label>
  );
}

export function Side(): ReactNode {
  const { place, goPlace, guest, host, openRoom } = usePage();
  const at = (p: Place) => place.kind === p.kind && ("id" in p ? "id" in place && place.id === p.id : true);
  return (
    <aside className="jp-side" aria-label={`With ${host.first}`}>
      <div className="jp-maya">
        <HostAvatar />
        <h1>{host.name}</h1>
      </div>
      <SearchBox />
      {guest.liveNow && place.kind !== "new" ? <LiveCard /> : null}
      <nav className="jp-nav" aria-label="Your conversations">
        {hasNews(guest) || place.kind === "new" ? <Item icon="sparkle" label="What's new" count={guest.news.length} on={at({ kind: "new" })} onClick={() => goPlace({ kind: "new" })} /> : null}
        <button type="button" className={at({ kind: "direct" }) ? "jp-it who on" : "jp-it who"} onClick={() => goPlace({ kind: "direct" })}>
          <Face name={host.name} photo={host.avatar} size={26} />
          <span className="jp-it-t">
            <b>{host.first}</b>
            {guest.directLast ? <small>{guest.directLast}</small> : null}
          </span>
          {guest.directUnread ? <i className="jp-dot" aria-label="New" /> : null}
        </button>
        <Item icon="fold" label="Files" on={at({ kind: "files" })} onClick={() => goPlace({ kind: "files" })} />
        <p className="jp-h">Your rooms</p>
        {guest.rooms.map((r) => (
          <div className="jp-room" key={r.webinarId}>
            <button type="button" className="jp-room-n" aria-label={`${r.title}, its sessions`} onClick={() => openRoom(r.webinarId)}>
              {r.posterUrl ? <img src={r.posterUrl} alt="" /> : <span className="ph" aria-hidden="true" />}
              <span>
                <b dir="auto">{r.title}</b>
                <small>{r.line}</small>
              </span>
            </button>
            {r.everyoneChat ? <Item icon="users" label="Everyone" count={r.everyoneNew} on={at({ kind: "everyone", id: r.webinarId })} onClick={() => goPlace({ kind: "everyone", id: r.webinarId })} /> : null}
            <Item icon="bell" label="Announcements" count={r.announceNew} on={at({ kind: "announce", id: r.webinarId })} onClick={() => goPlace({ kind: "announce", id: r.webinarId })} />
            {r.circles.map((c) => (
              <Item key={c.circleId} icon="target" label={c.name} count={c.newCount} live={c.live} on={at({ kind: "circle", id: c.circleId })} onClick={() => goPlace({ kind: "circle", id: c.circleId })} />
            ))}
          </div>
        ))}
      </nav>
      <MadeWith />
    </aside>
  );
}
